/**
 * Reportes del Seguimiento de transferencias de chasis
 * (backend: informetransferenciasbackend.py).
 *
 *   ReportePeriodoTransferencias  Una fila por transferencia en el rango de
 *     fechas de salida: unidades, recibidas, pendientes, devueltas, tiempo
 *     en tránsito y estado. Clic en la fila abre el detalle
 *     por chasis (quién recibió, cuándo y su comentario). Buscar por chasis
 *     ignora las fechas y trae toda la historia de esa unidad.
 *   ReporteTransito               Cada chasis que salió y no ha llegado, con
 *     días en camino por rango de antigüedad y una observación cuando el inventario no lo ubica en tránsito.
 *
 * Ambos imprimen (hoja carta, estilo de casa) y exportan a Excel. Un usuario
 * sin alcance total solo ve transferencias donde su sucursal envía o
 * recibe: el backend lo fuerza y aquí solo se avisa.
 */
import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import SearchIcon from "@mui/icons-material/Search";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useGlobalMDAlert } from "context";
import { FP, FpDataGrid, StatusPill, fpInputSx } from "components/financepro";
import { fechaDMY, useApi, useSucursales } from "lib/api";
import { imprimirListadoAgrupado, imprimirListadoGrande } from "lib/impresionhtml";

// Horas → "5 h" bajo un día, "2.5 d" desde ahí.
const fmtTiempo = (h) => {
  if (h === null || h === undefined || h === "") return "—";
  const n = Number(h);
  if (Number.isNaN(n)) return "—";
  return n < 24 ? `${Math.round(n)} h` : `${(n / 24).toFixed(1)} d`;
};

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioMesISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const codigo = (label) =>
  String(label || "")
    .split("-")[0]
    .trim();
const stamp = () => new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "");

const ESTADOS = ["En tránsito", "Parcial", "Completada", "Con devolución"];
const VARIANTE_ESTADO = {
  "En tránsito": "warning",
  Parcial: "info",
  Completada: "success",
  "Con devolución": "danger",
};
const VARIANTE_LINEA = { Completado: "success", "En progreso": "warning", Devuelta: "danger" };
const COLOR_RANGO = {
  "0-2 días": FP.color.successDark,
  "3-7 días": FP.color.info,
  "8-15 días": FP.color.warning,
  "Más de 15 días": FP.color.danger,
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = {
  ...btnBase,
  height: 46,
  width: "100%",
  bgcolor: FP.color.navy,
  color: "#FFFFFF",
  "&:hover": { bgcolor: FP.color.navySoft, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${FP.color.navy}66`, color: "#FFFFFF" },
};
const btnOutline = {
  ...btnBase,
  color: FP.color.navy,
  bgcolor: "#FFFFFF",
  border: `1px solid ${FP.color.navy}`,
  "&:hover": {
    bgcolor: `${FP.color.navy}12`,
    border: `1px solid ${FP.color.navy}`,
    color: FP.color.navy,
  },
};

function Indicador({ label, value, color = FP.color.textPrimary, activo, onClick, detalle }) {
  return (
    <Box
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (onClick && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        flex: "1 1 140px",
        minWidth: 0,
        px: 1.8,
        py: 1.2,
        borderRadius: 1.8,
        border: `1px solid ${activo ? color : FP.color.border}`,
        borderLeft: `4px solid ${color}`,
        bgcolor: activo ? `${color}10` : "#FFFFFF",
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.15s ease",
        "&:hover": onClick ? { bgcolor: `${color}0D` } : {},
      }}
    >
      <Typography
        sx={{
          fontSize: "0.64rem",
          fontWeight: 800,
          color: FP.color.textSecondary,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "1.15rem",
          fontWeight: 800,
          color,
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1.3,
        }}
      >
        {value}
      </Typography>
      {detalle ? (
        <Typography sx={{ fontSize: "0.72rem", color: FP.color.textSecondary, fontWeight: 600 }}>
          {detalle}
        </Typography>
      ) : null}
    </Box>
  );
}

function SelectorSucursal({ label, value, onChange, sucursales }) {
  return (
    <TextField
      select
      fullWidth
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={fpInputSx}
    >
      <MenuItem value="">Todas</MenuItem>
      {sucursales.map((s) => (
        <MenuItem key={s.label} value={codigo(s.label)}>
          {s.label}
        </MenuItem>
      ))}
    </TextField>
  );
}

function AvisoAlcance({ forzada }) {
  if (!forzada) return null;
  return (
    <Typography sx={{ fontSize: "0.8rem", color: FP.color.textSecondary, mb: 1 }}>
      Solo se muestran transferencias donde su sucursal ({forzada}) envía o recibe.
    </Typography>
  );
}

function BotonesSalida({ onImprimir, onExcel, disabled }) {
  return (
    <Stack direction="row" spacing={1}>
      <Button
        startIcon={<PrintOutlinedIcon />}
        onClick={onImprimir}
        disabled={disabled}
        sx={btnOutline}
      >
        Imprimir
      </Button>
      <Button
        startIcon={<FileDownloadOutlinedIcon />}
        onClick={onExcel}
        disabled={disabled}
        sx={btnOutline}
      >
        Excel
      </Button>
    </Stack>
  );
}

function descargarExcel(hojas, nombre) {
  const libro = XLSX.utils.book_new();
  hojas.forEach(({ titulo, aoa, anchos }) => {
    const hoja = XLSX.utils.aoa_to_sheet(aoa);
    if (anchos) hoja["!cols"] = anchos.map((wch) => ({ wch }));
    XLSX.utils.book_append_sheet(libro, hoja, titulo);
  });
  XLSX.writeFile(libro, `${nombre}_${stamp()}.xlsx`);
}

// ---------------------------------------------------------------------------
// Reporte por período
// ---------------------------------------------------------------------------

const COLS_PERIODO = [
  { field: "referencia", h: "Ref.", w: 95, get: (r) => r.referencia },
  { field: "fechasalida", h: "Salida", w: 130, get: (r) => r.fechasalida },
  { field: "origen", h: "Origen", flex: 1, minWidth: 130, get: (r) => r.origen },
  { field: "destino", h: "Destino", flex: 1, minWidth: 130, get: (r) => r.destino },
  { field: "transportista", h: "Transportista", w: 130, get: (r) => r.transportista },
  { field: "transmuser", h: "Enviado por", w: 110, get: (r) => r.transmuser },
  { field: "unidades", h: "Unid.", w: 70, right: true, get: (r) => r.unidades },
  { field: "recibidas", h: "Recib.", w: 70, right: true, get: (r) => r.recibidas },
  { field: "pendientes", h: "Pend.", w: 70, right: true, get: (r) => r.pendientes },
  { field: "devueltas", h: "Dev.", w: 65, right: true, get: (r) => r.devueltas },
  { field: "horas", h: "Tránsito", w: 85, right: true, get: (r) => fmtTiempo(r.horas) },
  { field: "fechallegada", h: "Llegada", w: 130, get: (r) => r.fechallegada || "—" },
  { field: "estado", h: "Estado", w: 135, get: (r) => r.estado, pill: true },
];

export function ReportePeriodoTransferencias() {
  const { post } = useApi();
  const { showAlert } = useGlobalMDAlert();
  const sucursales = useSucursales({ todas: true });
  const [fecha1, setFecha1] = useState(inicioMesISO());
  const [fecha2, setFecha2] = useState(hoyISO());
  const [origen, setOrigen] = useState("");
  const [destino, setDestino] = useState("");
  const [estado, setEstado] = useState("");
  const [transportista, setTransportista] = useState("");
  const [chasis, setChasis] = useState("");
  const [filas, setFilas] = useState([]);
  const [totales, setTotales] = useState(null);
  const [forzada, setForzada] = useState("");
  const [cargando, setCargando] = useState(false);
  const [detalle, setDetalle] = useState(null);

  const cargar = async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/transferencias_informe", {
        fechadesde: fecha1,
        fechahasta: fecha2,
        origen,
        destino,
        estado,
        transportista: transportista.trim(),
        chasis: chasis.trim(),
      });
      if (ok) {
        setFilas((data.filas || []).map((f) => ({ ...f, id: f.idtransaccion })));
        setTotales(data.totales || null);
        setForzada(data.sucursalforzada || "");
      } else {
        setFilas([]);
        setTotales(null);
        showAlert({ message: data.Error || "No se pudo generar el reporte", color: "error" });
      }
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nombreSuc = (c) => sucursales.find((s) => codigo(s.label) === c)?.label || c;
  const subtitulo = [
    chasis.trim()
      ? `Chasis "${chasis.trim()}" (todas las fechas)`
      : `Salidas del ${fechaDMY(fecha1)} al ${fechaDMY(fecha2)}`,
    origen ? `Origen ${nombreSuc(origen)}` : "",
    destino ? `Destino ${nombreSuc(destino)}` : "",
    estado ? `Estado ${estado}` : "",
    transportista.trim() ? `Transportista "${transportista.trim()}"` : "",
  ]
    .filter(Boolean)
    .join(" · ");

  const t = totales || {};
  const filaTotal = () =>
    COLS_PERIODO.map((c) => {
      if (c.field === "referencia") return "TOTAL";
      if (["unidades", "recibidas", "pendientes", "devueltas"].includes(c.field))
        return t[c.field] ?? 0;
      if (c.field === "horas")
        return t.horaspromedio != null ? `Prom. ${fmtTiempo(t.horaspromedio)}` : "";
      return "";
    });

  const imprimir = () =>
    imprimirListadoGrande({
      titulo: "Transferencias de chasis",
      subtitulo,
      cabeceras: COLS_PERIODO.map((c) => c.h),
      filas: filas.map((r) => COLS_PERIODO.map((c) => c.get(r))),
      totales: filaTotal(),
      numericas: COLS_PERIODO.map((c, i) => (c.right ? i : -1)).filter((i) => i >= 0),
    });

  const exportar = () =>
    descargarExcel(
      [
        {
          titulo: "Transferencias",
          aoa: [
            ["Transferencias de chasis"],
            [subtitulo],
            [],
            [...COLS_PERIODO.map((c) => c.h), "Recibido por", "Comentario", "Devolución de"],
            ...filas.map((r) => [
              ...COLS_PERIODO.map((c) => c.get(r)),
              r.receptores,
              r.comentario,
              r.devolucionde,
            ]),
            [],
            filaTotal(),
          ],
          anchos: [10, 16, 20, 20, 16, 14, 7, 7, 7, 7, 10, 16, 15, 18, 30, 12],
        },
        {
          titulo: "Detalle por chasis",
          aoa: [
            [
              "Ref.",
              "Salida",
              "Origen",
              "Destino",
              "Chasis",
              "Marca",
              "Modelo",
              "Color",
              "Estado",
              "Recibido por",
              "Fecha recepción",
              "Comentario recepción",
            ],
            ...filas.flatMap((r) =>
              (r.detalle || []).map((d) => [
                r.referencia,
                r.fechasalida,
                r.origen,
                r.destino,
                d.chasis,
                d.marca,
                d.modelo,
                d.color,
                d.status,
                d.receptoruser,
                d.fecharecep,
                d.comenreceptor,
              ])
            ),
          ],
          anchos: [10, 16, 18, 18, 22, 12, 12, 10, 12, 14, 16, 30],
        },
      ],
      "Transferencias"
    );

  const columns = COLS_PERIODO.map((c) => ({
    field: c.field,
    headerName: c.h,
    ...(c.flex ? { flex: c.flex, minWidth: c.minWidth } : { width: c.w }),
    ...(c.right ? { align: "right", headerAlign: "right" } : {}),
    valueGetter: (_v, row) => (c.field === "horas" ? row.horas : c.get(row)),
    ...(c.field === "horas" ? { renderCell: (p) => c.get(p.row) } : {}),
    ...(c.pill
      ? {
          renderCell: (p) => (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: "100%" }}>
              <StatusPill
                label={p.row.estado}
                variant={VARIANTE_ESTADO[p.row.estado] || "neutral"}
              />
              {p.row.devolucionde ? <StatusPill label="Retorno" variant="neutral" /> : null}
            </Stack>
          ),
        }
      : {}),
  }));

  return (
    <Box>
      <Grid container spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
        <Grid item xs={6} sm={4} md={1.5}>
          <TextField
            fullWidth
            type="date"
            label="Desde"
            InputLabelProps={{ shrink: true }}
            value={fecha1}
            onChange={(e) => setFecha1(e.target.value)}
            sx={fpInputSx}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={1.5}>
          <TextField
            fullWidth
            type="date"
            label="Hasta"
            InputLabelProps={{ shrink: true }}
            value={fecha2}
            onChange={(e) => setFecha2(e.target.value)}
            sx={fpInputSx}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={1.75}>
          <SelectorSucursal
            label="Origen"
            value={origen}
            onChange={setOrigen}
            sucursales={sucursales}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={1.75}>
          <SelectorSucursal
            label="Destino"
            value={destino}
            onChange={setDestino}
            sucursales={sucursales}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={1.5}>
          <TextField
            select
            fullWidth
            label="Estado"
            value={estado}
            onChange={(e) => setEstado(e.target.value)}
            sx={fpInputSx}
          >
            <MenuItem value="">Todos</MenuItem>
            {ESTADOS.map((e) => (
              <MenuItem key={e} value={e}>
                {e}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={4} md={1.5}>
          <TextField
            fullWidth
            label="Transportista"
            value={transportista}
            onChange={(e) => setTransportista(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && cargar()}
            sx={fpInputSx}
          />
        </Grid>
        <Grid item xs={8} sm={4} md={1.5}>
          <TextField
            fullWidth
            label="Chasis"
            value={chasis}
            onChange={(e) => setChasis(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && cargar()}
            sx={fpInputSx}
          />
        </Grid>
        <Grid item xs={4} sm={4} md={1}>
          <Button onClick={cargar} disabled={cargando} sx={btnSolid} aria-label="Procesar">
            {cargando ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
          </Button>
        </Grid>
      </Grid>

      <AvisoAlcance forzada={forzada} />

      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.2} sx={{ mb: 1.5 }}>
        <Indicador label="Transferencias" value={t.transferencias ?? 0} color={FP.color.navy} />
        <Indicador label="Unidades" value={t.unidades ?? 0} color={FP.color.navy} />
        <Indicador label="Recibidas" value={t.recibidas ?? 0} color={FP.color.successDark} />
        <Indicador label="En tránsito" value={t.pendientes ?? 0} color={FP.color.warning} />
        <Indicador label="Devueltas" value={t.devueltas ?? 0} color={FP.color.danger} />
        <Indicador
          label="Tránsito promedio"
          value={fmtTiempo(t.horaspromedio)}
          detalle="de las ya recibidas"
          color={FP.color.info}
        />
      </Stack>

      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: "0.82rem", color: FP.color.textSecondary, flex: 1 }}>
          {subtitulo}. Haga clic en una fila para ver el detalle por chasis.
        </Typography>
        <BotonesSalida onImprimir={imprimir} onExcel={exportar} disabled={!filas.length} />
      </Stack>

      <Box sx={{ bgcolor: "#fff", overflowX: "auto" }}>
        <FpDataGrid
          autoHeight
          rows={filas}
          columns={columns}
          loading={cargando}
          onRowClick={(p) => setDetalle(p.row)}
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          vacioTitulo="Sin transferencias en el rango"
          vacioSubtitulo='Ajuste los filtros y haga clic en "Procesar".'
          sx={{ minWidth: 1100, "& .MuiDataGrid-row": { cursor: "pointer" } }}
        />
      </Box>

      <DetalleTransferencia fila={detalle} onClose={() => setDetalle(null)} />
    </Box>
  );
}

function Dato({ k, v }) {
  if (!v && v !== 0) return null;
  return (
    <Box>
      <Typography
        sx={{
          fontSize: "0.64rem",
          fontWeight: 800,
          color: FP.color.textSecondary,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}
      >
        {k}
      </Typography>
      <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: FP.color.textPrimary }}>
        {v}
      </Typography>
    </Box>
  );
}

function DetalleTransferencia({ fila, onClose }) {
  const columnas = useMemo(
    () => [
      { field: "chasis", headerName: "Chasis", width: 190 },
      {
        field: "vehiculo",
        headerName: "Vehículo",
        flex: 1,
        minWidth: 150,
        valueGetter: (_v, r) => [r.marca, r.modelo, r.color].filter(Boolean).join(" · "),
      },
      {
        field: "status",
        headerName: "Estado",
        width: 120,
        renderCell: (p) => (
          <Stack direction="row" alignItems="center" sx={{ height: "100%" }}>
            <StatusPill label={p.row.status} variant={VARIANTE_LINEA[p.row.status] || "neutral"} />
          </Stack>
        ),
      },
      { field: "receptoruser", headerName: "Recibido por", width: 115 },
      { field: "fecharecep", headerName: "Fecha", width: 130 },
      { field: "comenreceptor", headerName: "Comentario recepción", flex: 1, minWidth: 160 },
    ],
    []
  );
  if (!fila) return null;
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2.5 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", color: FP.color.textPrimary }}>
            Transferencia {fila.referencia}
          </Typography>
          <StatusPill label={fila.estado} variant={VARIANTE_ESTADO[fila.estado] || "neutral"} />
        </Stack>
        <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary, fontWeight: 600 }}>
          {fila.origen} → {fila.destino}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={3} sx={{ mb: 2 }}>
          <Dato k="Salida" v={fila.fechasalida} />
          <Dato k="Enviado por" v={fila.transmuser} />
          <Dato k="Transportista" v={fila.transportista} />
          <Dato k="Llegada" v={fila.fechallegada} />
          <Dato k="Recibido por" v={fila.receptores} />
          <Dato k="Tiempo en tránsito" v={fmtTiempo(fila.horas)} />
          <Dato k="Devolución de" v={fila.devolucionde} />
        </Stack>
        {fila.comentario ? (
          <Box
            sx={{
              mb: 2,
              px: 1.6,
              py: 1,
              borderLeft: `4px solid ${FP.color.navy}`,
              bgcolor: FP.color.surface,
              fontSize: "0.85rem",
            }}
          >
            <b>Comentario de salida:</b> {fila.comentario}
          </Box>
        ) : null}
        <FpDataGrid
          autoHeight
          rows={fila.detalle || []}
          columns={columnas}
          hideFooter={(fila.detalle || []).length <= 25}
          getRowId={(r) => r.id ?? r.chasis}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={btnOutline}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Unidades en tránsito
// ---------------------------------------------------------------------------

const COLS_TRANSITO = [
  { field: "referencia", h: "Ref.", w: 95, get: (r) => r.referencia },
  { field: "chasis", h: "Chasis", w: 185, get: (r) => r.chasis },
  {
    field: "vehiculo",
    h: "Vehículo",
    flex: 1,
    minWidth: 150,
    get: (r) => [r.marca, r.modelo, r.color].filter(Boolean).join(" · "),
  },
  { field: "origen", h: "Origen", w: 135, get: (r) => r.origen },
  { field: "destino", h: "Destino", w: 135, get: (r) => r.destino },
  { field: "fechasalida", h: "Salida", w: 130, get: (r) => r.fechasalida },
  { field: "dias", h: "Días", w: 70, right: true, get: (r) => r.dias },
  { field: "transportista", h: "Transportista", w: 125, get: (r) => r.transportista },
  { field: "transmuser", h: "Enviado por", w: 110, get: (r) => r.transmuser },
  { field: "observacion", h: "Observación", flex: 1, minWidth: 170, get: (r) => r.observacion },
];

export function ReporteTransito() {
  const { post } = useApi();
  const { showAlert } = useGlobalMDAlert();
  const sucursales = useSucursales({ todas: true });
  const [origen, setOrigen] = useState("");
  const [destino, setDestino] = useState("");
  const [transportista, setTransportista] = useState("");
  const [chasis, setChasis] = useState("");
  const [filas, setFilas] = useState([]);
  const [rangos, setRangos] = useState([]);
  const [totales, setTotales] = useState(null);
  const [forzada, setForzada] = useState("");
  const [rango, setRango] = useState("");
  const [soloInconsistentes, setSoloInconsistentes] = useState(false);
  const [cargando, setCargando] = useState(false);

  const cargar = async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/transferencias_transito", {
        origen,
        destino,
        transportista: transportista.trim(),
        chasis: chasis.trim(),
      });
      if (ok) {
        setFilas(data.filas || []);
        setRangos(data.rangos || []);
        setTotales(data.totales || null);
        setForzada(data.sucursalforzada || "");
      } else {
        setFilas([]);
        setRangos([]);
        setTotales(null);
        showAlert({ message: data.Error || "No se pudo generar el reporte", color: "error" });
      }
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibles = filas.filter(
    (f) => (!rango || f.rango === rango) && (!soloInconsistentes || f.observacion)
  );
  const t = totales || {};
  const nombreSuc = (c) => sucursales.find((s) => codigo(s.label) === c)?.label || c;
  const subtitulo = [
    `Al ${new Date().toLocaleDateString("es-DO")}`,
    origen ? `Origen ${nombreSuc(origen)}` : "",
    destino ? `Destino ${nombreSuc(destino)}` : "",
    transportista.trim() ? `Transportista "${transportista.trim()}"` : "",
    chasis.trim() ? `Chasis "${chasis.trim()}"` : "",
    rango ? `Rango ${rango}` : "",
    soloInconsistentes ? "Solo inconsistencias" : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const totalFila = (etiqueta, unidades) =>
    COLS_TRANSITO.map((c, i) => {
      if (i === 0) return etiqueta;
      if (c.field === "chasis") return `${unidades} unidades`;
      return "";
    });

  const imprimir = () =>
    imprimirListadoAgrupado({
      titulo: "Unidades en tránsito",
      subtitulo,
      pagina: "letter landscape",
      cabeceras: COLS_TRANSITO.map((c) => c.h),
      grupos: rangos.map((r) => {
        const del = visibles.filter((f) => f.rango === r.rango);
        return {
          titulo: r.rango,
          resumen: `${del.length} unidades`,
          filas: del.map((f) => COLS_TRANSITO.map((c) => c.get(f))),
        };
      }),
      totales: totalFila("TOTAL", visibles.length),
      numericas: COLS_TRANSITO.map((c, i) => (c.right ? i : -1)).filter((i) => i >= 0),
    });

  const exportar = () =>
    descargarExcel(
      [
        {
          titulo: "En tránsito",
          aoa: [
            ["Unidades en tránsito"],
            [subtitulo],
            [],
            [...COLS_TRANSITO.map((c) => c.h), "Rango", "Precio", "Condición", "Comentario"],
            ...visibles.map((f) => [
              ...COLS_TRANSITO.map((c) => c.get(f)),
              f.rango,
              f.precio,
              f.condicion,
              f.comentario,
            ]),
            [],
            COLS_TRANSITO.map((c, i) => {
              if (i === 0) return "TOTAL";
              if (c.field === "chasis") return visibles.length;
              return "";
            }),
          ],
          anchos: [10, 22, 24, 18, 18, 16, 6, 16, 14, 32, 14, 12, 10, 30],
        },
      ],
      "UnidadesEnTransito"
    );

  const columns = COLS_TRANSITO.map((c) => ({
    field: c.field,
    headerName: c.h,
    ...(c.flex ? { flex: c.flex, minWidth: c.minWidth } : { width: c.w }),
    ...(c.right ? { align: "right", headerAlign: "right" } : {}),
    valueGetter: (_v, row) => c.get(row),
    ...(c.field === "dias"
      ? {
          renderCell: (p) => (
            <Typography
              component="span"
              sx={{ fontWeight: 800, fontSize: "0.85rem", color: COLOR_RANGO[p.row.rango] }}
            >
              {p.row.dias}
            </Typography>
          ),
        }
      : {}),
    ...(c.field === "observacion"
      ? {
          renderCell: (p) =>
            p.row.observacion ? (
              <Typography
                component="span"
                sx={{ fontSize: "0.8rem", fontWeight: 700, color: FP.color.danger }}
              >
                {p.row.observacion}
              </Typography>
            ) : (
              ""
            ),
        }
      : {}),
  }));

  return (
    <Box>
      <Grid container spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
        <Grid item xs={6} sm={4} md={2.5}>
          <SelectorSucursal
            label="Origen"
            value={origen}
            onChange={setOrigen}
            sucursales={sucursales}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2.5}>
          <SelectorSucursal
            label="Destino"
            value={destino}
            onChange={setDestino}
            sucursales={sucursales}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <TextField
            fullWidth
            label="Transportista"
            value={transportista}
            onChange={(e) => setTransportista(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && cargar()}
            sx={fpInputSx}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <TextField
            fullWidth
            label="Chasis"
            value={chasis}
            onChange={(e) => setChasis(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && cargar()}
            sx={fpInputSx}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={1}>
          <Button onClick={cargar} disabled={cargando} sx={btnSolid} aria-label="Procesar">
            {cargando ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
          </Button>
        </Grid>
      </Grid>

      <AvisoAlcance forzada={forzada} />

      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.2} sx={{ mb: 1.2 }}>
        <Indicador label="Unidades en tránsito" value={t.unidades ?? 0} color={FP.color.warning} />
        <Indicador
          label="Transferencias abiertas"
          value={t.transferencias ?? 0}
          color={FP.color.navy}
        />
        <Indicador
          label="Inconsistencias"
          value={t.inconsistentes ?? 0}
          color={FP.color.danger}
          detalle={soloInconsistentes ? "Filtrando · clic para quitar" : "Clic para filtrar"}
          activo={soloInconsistentes}
          onClick={() => setSoloInconsistentes((v) => !v)}
        />
      </Stack>

      <Typography
        sx={{
          fontSize: "0.7rem",
          fontWeight: 800,
          color: FP.color.textSecondary,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          mb: 0.8,
        }}
      >
        Antigüedad · clic para filtrar
      </Typography>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.2} sx={{ mb: 1.5 }}>
        {rangos.map((r) => (
          <Indicador
            key={r.rango}
            label={r.rango}
            value={r.unidades}
            color={COLOR_RANGO[r.rango]}
            activo={rango === r.rango}
            onClick={() => setRango((v) => (v === r.rango ? "" : r.rango))}
          />
        ))}
      </Stack>

      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: "0.82rem", color: FP.color.textSecondary, flex: 1 }}>
          {visibles.length} de {filas.length} unidades · {subtitulo}
        </Typography>
        <BotonesSalida onImprimir={imprimir} onExcel={exportar} disabled={!visibles.length} />
      </Stack>

      <Box sx={{ bgcolor: "#fff", overflowX: "auto" }}>
        <FpDataGrid
          autoHeight
          rows={visibles}
          columns={columns}
          loading={cargando}
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          vacioTitulo="No hay unidades en tránsito"
          vacioSubtitulo="Todo lo transferido ya fue recibido con estos filtros."
          sx={{ minWidth: 1200 }}
        />
      </Box>
    </Box>
  );
}
